<template>
  <div class="inline-flex">
    <BaseButton small primary @click="setShow(true)"> Edit Channel </BaseButton>
    <Portal to="app-dialog" v-if="show">
      <VModal ref="modal" @modal:hidden="setShow(false)" disable-backdrop-click>
        <div class="rounded shadow-xs w-full max-w-lg mx-auto" @click.stop>
          <div class="rounded shadow-lg bg-white overflow-hidden divide-y divide-gray-200">
            <div class="h-10 bg-white pl-4">
              <div class="flex items-center justify-between">
                <div class="flex-1">
                  <h1 class="text-sm">Edit Channel</h1>
                </div>
                <div class="flex-none">
                  <button
                    class="h-10 w-10 inline-flex items-center justify-center hover:bg-gray-200 focus:outline-none"
                    @click="$refs.modal.close()"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" class="inline-flex w-5 h-5">
                      <path
                        d="M12 10.586l4.95-4.95 1.414 1.414-4.95
                        4.95 4.95 4.95-1.414 1.414-4.95-4.95-4.95
                        4.95-1.414-1.414 4.95-4.95-4.95-4.95L7.05
                        5.636z"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            <div class="p-4">
              <BaseVeeForm :action="updateChannel" v-slot="{ isPending, invalid }">
                <div class="space-y-6">
                  <div class="space-y-4">
                    <ValidationProvider rules="required" name="type" slim vid="channableType">
                      <template #default="{ errors }">
                        <BaseFormGroup>
                          <BaseFormLabel for="channableType"> Channel type </BaseFormLabel>
                          <div>
                            <BaseFormSelect
                              id="channableType"
                              v-model="formData['channableType']"
                              :options="[
                                { label: 'Facebook', value: 'facebook_campaign' },
                                { label: 'Google Ad', value: 'ga_campaign' },
                              ]"
                              :invalid="errors.length > 0"
                              required
                            />
                          </div>
                          <BaseFormInlineMessage v-if="errors.length" danger>
                            {{ errors[0] }}
                          </BaseFormInlineMessage>
                        </BaseFormGroup>
                      </template>
                    </ValidationProvider>

                    <ValidationProvider name="channel id" slim vid="channableId">
                      <template #default="{ errors }">
                        <BaseFormGroup>
                          <BaseFormLabel for="channableId"> Channel Id </BaseFormLabel>
                          <div>
                            <BaseFormInput
                              id="channableId"
                              v-model="formData['channableId']"
                              :invalid="errors.length > 0"
                            />
                          </div>
                          <BaseFormInlineMessage v-if="errors.length" danger>
                            {{ errors[0] }}
                          </BaseFormInlineMessage>
                        </BaseFormGroup>
                      </template>
                    </ValidationProvider>

                    <ValidationProvider name="gross profit factor" slim vid="grossProfitFactor">
                      <template #default="{ errors }">
                        <BaseFormGroup>
                          <BaseFormLabel for="grossProfitFactor">
                            Gross profit factor
                          </BaseFormLabel>
                          <div>
                            <BaseFormInput
                              id="grossProfitFactor"
                              type="number"
                              min="0"
                              step="0.01"
                              v-model="formData['grossProfitFactor']"
                              :invalid="errors.length > 0"
                              class="w-32"
                            />
                          </div>
                          <BaseFormInlineMessage v-if="errors.length" danger>
                            {{ errors[0] }}
                          </BaseFormInlineMessage>
                        </BaseFormGroup>
                      </template>
                    </ValidationProvider>

                    <ValidationProvider name="page" slim vid="pageUuid">
                      <template #default="{ errors }">
                        <BaseFormGroup>
                          <BaseFormLabel for="page"> Page </BaseFormLabel>
                          <div>
                            <BaseFormSelect
                              id="page"
                              v-model="formData['pageUuid']"
                              :options="pageOptions"
                              :invalid="errors.length > 0"
                              required
                            />
                          </div>
                          <BaseFormInlineMessage v-if="errors.length" danger>
                            {{ errors[0] }}
                          </BaseFormInlineMessage>
                        </BaseFormGroup>
                      </template>
                    </ValidationProvider>
                  </div>

                  <div class="flex space-x-2">
                    <BaseButton primary :disabled="isPending || invalid"> Update </BaseButton>
                  </div>
                </div>
              </BaseVeeForm>
            </div>
          </div>
        </div>
      </VModal>
    </Portal>
  </div>
</template>

<script>
import { isPlainObject, pick } from 'lodash-es';

export default {
  props: {
    channel: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      show: false,
      formData: {},
    };
  },
  computed: {
    project() {
      return this.channel.project;
    },
    projectPages() {
      return this.project?.pages || [];
    },
    pageOptions() {
      return this.projectPages.map((page) => ({
        value: page.id,
        label: page.title,
      }));
    },
  },
  watch: {
    channel: 'setFormData',
  },
  created() {
    this.setFormData(this.channel);
  },
  methods: {
    setFormData(defaultData) {
      if (isPlainObject(defaultData)) {
        this.formData = {
          ...this.formData,
          ...pick(defaultData, ['channableId', 'channableType', 'grossProfitFactor', 'pageUuid']),
        };
      }
    },
    setShow(value) {
      this.show = value;
    },
    async updateChannel() {
      try {
        await this.$store.dispatch({
          type: 'ui/channels/update',
          channelId: this.channel.id,
          formData: this.formData,
        });

        this.$emit('updated');

        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
  },
};
</script>
